import authorities from '../londonAuthorities.json';
import { qualifiedLondonDocumentSource } from './londonDocumentSources';
import { londonScopeIsPublic, usesSharedLondonDelivery } from '../planning/londonSharedDelivery';
import { readSharedLondonPayload, sharedLondonFeedParameters, SharedLondonError } from './sharedLondonPlanningService';

export type LondonCouncil = keyof typeof authorities;
export const LONDON_AUTHORITIES = authorities;
export const LONDON_API_ORIGIN = 'https://api.city-scope.co.uk/api/v1/london';
export const LONDON_POSTCODE_ATTRIBUTION = [
  'Contains OS data © Crown copyright and database right 2026',
  'Contains Royal Mail data © Royal Mail copyright and database right 2026',
  'Source: Office for National Statistics licensed under the Open Government Licence v.3.0',
];
export type LondonBounds = { west: number; south: number; east: number; north: number };
type Transport = { fetchImpl?: typeof fetch; signal?: AbortSignal; backend?: boolean };
export type LondonFeedOptions = Transport & {
  council?: string; query?: string; limit?: number; cursor?: string | null;
  start?: string; end?: string; bounds?: LondonBounds;
  filters?: Record<string,unknown>; boundsEndExclusive?: boolean;
};

async function sharedOperation<T>(operation: () => Promise<T>): Promise<T> {
  try { return await operation(); }
  catch (error) {
    if (error instanceof SharedLondonError) throw new LondonFeedError(error.message,error.status);
    throw error;
  }
}

function requirePublicLondonScope(scope?: string) {
  if ((!scope || isLondonScope(scope)) && !londonScopeIsPublic(scope)) throw new LondonFeedError('London scope is not public',404);
}

export class LondonFeedError extends Error {
  constructor(message: string, public status = 503) { super(message); this.name = 'LondonFeedError'; }
}

const DOCUMENT_STATES = ['not_checked','complete_nonempty','verified_empty','partial','blocked','failed'];
const SOURCE_REFRESH_POLICY = 'complete_capture_retained_source_holds_v1';
const SOURCE_REFRESH_STATES = ['held','held_owner_changed_requires_reconciliation','verified_current_capture','current_owner_not_verified_in_capture'];

export type LondonSourceRefresh = {
  policy: string; status: string; last_checked_at: string; source_system: string;
  source_manifest_sha256: string; window_start: string; window_end_exclusive: string;
  retained_source_facts: boolean; reason: string | null;
};

function sourceRefreshDetail(row: Record<string,any>): { source_refresh?: LondonSourceRefresh } {
  if (row.source_refresh === undefined) return {};
  const value = row.source_refresh;
  const held = ['held','held_owner_changed_requires_reconciliation'].includes(value?.status);
  try {
    const validated = typeof row.validated_date === 'string' ? day(row.validated_date.slice(0,10)) : undefined;
    if (!value || value.policy !== SOURCE_REFRESH_POLICY || !SOURCE_REFRESH_STATES.includes(value.status) ||
        typeof value.last_checked_at !== 'string' || !/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(value.last_checked_at) ||
        !Number.isFinite(Date.parse(value.last_checked_at)) || value.source_system !== row.source_system ||
        typeof value.source_manifest_sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(value.source_manifest_sha256) ||
        typeof value.window_start !== 'string' || typeof value.window_end_exclusive !== 'string' ||
        !day(value.window_start) || !day(value.window_end_exclusive) || value.window_start >= value.window_end_exclusive ||
        !validated || validated < value.window_start || validated >= value.window_end_exclusive ||
        value.retained_source_facts !== held || value.reason !== (held ? 'not_returned_by_complete_source_query' : null)) {
      throw new Error('invalid_source_refresh');
    }
  } catch (_) { throw new LondonFeedError('London source freshness could not be verified'); }
  return { source_refresh: {
    policy: value.policy, status: value.status, last_checked_at: value.last_checked_at, source_system: value.source_system,
    source_manifest_sha256: value.source_manifest_sha256, window_start: value.window_start, window_end_exclusive: value.window_end_exclusive,
    retained_source_facts: value.retained_source_facts, reason: value.reason,
  } };
}

export function londonSourceRefreshNotice(refresh?: Pick<LondonSourceRefresh,'status'>) {
  if (refresh?.status === 'held') {
    return 'This application was missing from the latest complete council list. Previously collected information is still shown. Check the council record for its current status.';
  }
  if (refresh?.status === 'held_owner_changed_requires_reconciliation') {
    return 'The latest council check needs reconciliation with these application details. Check the council record for its current status.';
  }
  if (refresh?.status === 'current_owner_not_verified_in_capture') {
    return 'The latest council capture has not verified these application details. Check the council record for the latest information.';
  }
  return null;
}

type ReportedSourceFacts = {
  source_system: 'london-datahub'; source_url: string; source_record_sha256: string;
  source_manifest_sha256: string; source_captured_at: string;
  decision: string|null; decision_date: string|null; status: string|null;
  meaning: 'last_reported_by_GLA_not_verified_by_native_results_list';
};

function reportedSourceDetail(row: Record<string,any>) {
  if (row.reported_source_facts === undefined && row.field_provenance === undefined) {
    return {reported_source_facts:undefined,field_provenance:undefined};
  }
  const value=row.reported_source_facts;
  try {
    const nativeSource = row.source_system==='idox' ||
      (row.source_council==='haringey' && row.source_system==='haringey-arcus');
    if (!value || !nativeSource || value.source_system!=='london-datahub' ||
        value.meaning!=='last_reported_by_GLA_not_verified_by_native_results_list' ||
        !/^[0-9a-f]{64}$/.test(value.source_record_sha256) || !/^[0-9a-f]{64}$/.test(value.source_manifest_sha256) ||
        typeof value.source_captured_at!=='string' || !/(Z|[+-]\d{2}:\d{2})$/.test(value.source_captured_at) ||
        !Number.isFinite(Date.parse(value.source_captured_at)) || new URL(value.source_url).protocol!=='https:' ||
        (value.decision!==null && typeof value.decision!=='string') ||
        (value.status!==null && typeof value.status!=='string') ||
        (value.decision_date!==null && !day(value.decision_date))) throw new Error('invalid_reported_source');
    const reported: ReportedSourceFacts={
      source_system:value.source_system,source_url:value.source_url,source_record_sha256:value.source_record_sha256,
      source_manifest_sha256:value.source_manifest_sha256,source_captured_at:value.source_captured_at,
      decision:value.decision,decision_date:value.decision_date,status:value.status,meaning:value.meaning,
    };
    const provenance: Record<string,any>={};
    if (row.field_provenance !== undefined) {
      if (!row.field_provenance || Array.isArray(row.field_provenance) || typeof row.field_provenance!=='object') throw new Error('invalid_field_provenance');
      for (const [field,evidence] of Object.entries(row.field_provenance)) {
        if (!['decision','decision_date'].includes(field) || !evidence || typeof evidence!=='object' ||
            reported[field as 'decision'|'decision_date']===null || row[field]!==reported[field as 'decision'|'decision_date'] ||
            row.field_holds?.[field]?.reason!=='not_supplied_by_source_list' ||
            !['Decided','Appeal decided','Appeal lodged','Unknown'].includes(row.status)) throw new Error('invalid_field_provenance');
        const source=Object.fromEntries(Object.entries(reported).filter(([key])=>!['decision','decision_date','status'].includes(key)));
        if (Object.entries(source).some(([key,expected])=>(evidence as any)[key]!==expected)) throw new Error('invalid_field_provenance');
        provenance[field]=source;
      }
    }
    return {reported_source_facts:reported,field_provenance:Object.keys(provenance).length ? provenance : undefined};
  } catch (_) { throw new LondonFeedError('London reported decision source could not be verified'); }
}

export function londonReportedDecisionNotice(facts?: ReportedSourceFacts) {
  if (!facts || (!facts.decision && !facts.decision_date)) return null;
  const decision=facts.decision || 'a decision date';
  const date=facts.decision_date ? ` (${new Date(facts.decision_date+'T12:00:00Z').toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})})` : '';
  return `GLA last reported: ${decision}${date}. The council's current status takes precedence; its result list has not verified these decision details.`;
}

/** A missing document field is unknown, including on the older London API. */
function documentDetail(row: Record<string,any>) {
  if (row.document_index === undefined) return {};
  const index = row.document_index;
  const docs = row.documents;
  if (!index || !DOCUMENT_STATES.includes(index.status) || !Array.isArray(docs) || docs.length>1000 ||
      (index.document_count!==null && (!Number.isInteger(index.document_count) || index.document_count!==docs.length)) ||
      (index.status==='not_checked' && (docs.length!==0 || index.document_count!==null)) ||
      (index.status==='verified_empty' && (docs.length!==0 || index.document_count!==0)) ||
      (index.status==='complete_nonempty' && (!docs.length || index.document_count!==docs.length))) {
    throw new LondonFeedError('London document list could not be verified');
  }
  const identities = new Set<string>();
  const documents = docs.map((doc:any) => {
    let url:URL, source:URL;
    try { url=new URL(doc.link);source=new URL(row.source_url); }
    catch (_) { throw new LondonFeedError('London document source could not be verified'); }
    const fileUrl=`${LONDON_API_ORIGIN}/documents/${doc.document_uid}/file`;
    const previewUrl=`${LONDON_API_ORIGIN}/documents/${doc.document_uid}/preview`;
    const cached=doc.link===fileUrl;
    if (cached) {
      let original:URL;
      try { original=new URL(doc.source_url); }
      catch (_) { throw new LondonFeedError('London document source could not be verified'); }
      if (doc.format!=='pdf' ||
          doc.file_url!==fileUrl || doc.preview_url!==previewUrl ||
          (doc.thumbnail_url!==undefined && doc.thumbnail_url!==previewUrl) ||
          doc.readability!=='structurally_readable' || !/^[0-9a-f]{64}$/.test(doc.PDF_sha256) ||
          !/^[0-9a-f]{64}$/.test(doc.document_version_sha256) ||
          !Number.isInteger(doc.PDF_bytes) || doc.PDF_bytes<1 || doc.PDF_bytes>8_000_000 ||
          !Number.isInteger(doc.PDF_pages) || doc.PDF_pages<1 || doc.PDF_pages>200 ||
          typeof doc.file_verified_at!=='string' || !/(Z|[+-]\d{2}:\d{2})$/.test(doc.file_verified_at) ||
          !Number.isFinite(Date.parse(doc.file_verified_at)) ||
          !qualifiedLondonDocumentSource(row,doc,doc.source_url,true)) {
        throw new LondonFeedError('London document source could not be verified');
      }
    }
    if (doc.application_uid!==row.application_uid || doc.source_council!==row.source_council ||
        typeof doc.document_uid!=='string' || !/^london-doc:[0-9a-f]{64}$/.test(doc.document_uid) ||
        identities.has(doc.document_uid) || url.protocol!=='https:' || (!cached && !qualifiedLondonDocumentSource(row,doc,doc.link,false)) ||
        (!cached && (doc.file_url!=null || doc.preview_url!=null || doc.thumbnail_url!=null ||
          (doc.source_url!==undefined && doc.source_url!==doc.link))) ||
        source.protocol!=='https:' || url.username || url.password || url.port || url.hash) {
      throw new LondonFeedError('London document source could not be verified');
    }
    identities.add(doc.document_uid);
    const title=doc.desc || doc.doc_type || doc.filename || 'Planning document';
    return { ...doc,doc_link:doc.link,document_url:doc.link,source_desc:title,title,published_at:doc.date_published ?? null,
      thumbnail_url:doc.preview_url ?? null };
  });
  return { documents,document_index:{ ...index } };
}

export function londonDocumentListNotice(index?: { status?: string; retained_previous_verified_list?: boolean }) {
  if (index?.status==='complete_nonempty') return null;
  if (index?.status==='verified_empty') return 'The council document list was checked and contained no documents.';
  if (['partial','blocked','failed'].includes(index?.status || '')) {
    return index?.retained_previous_verified_list
      ? 'The latest document check could not be completed. Previously checked documents are still shown.'
      : 'The document check could not be completed. Check the council record for documents.';
  }
  return 'Documents have not been checked yet. Check the council record for drawings and supporting documents.';
}

/** Keep a cursor on the source's declared validation window across midnight. */
export function londonPageWindow(page: {
  date_basis?: unknown; window_start?: unknown; window_end_exclusive?: unknown;
}, expected: { start?: string; end?: string } = {}) {
  const start = page.window_start;
  const end = page.window_end_exclusive;
  try {
    if (page.date_basis !== 'validated' || typeof start !== 'string' || typeof end !== 'string' ||
        !day(start) || !day(end) || start >= end ||
        (expected.start !== undefined && start !== expected.start) ||
        (expected.end !== undefined && end !== expected.end)) throw new Error('invalid_window');
  } catch (_) {
    throw new LondonFeedError('London source validation window could not be verified');
  }
  return { start, end };
}

export function isLondonScope(value: unknown): boolean {
  return typeof value === 'string' && (value === 'london' || Object.hasOwn(authorities,value));
}

function council(value?: string): LondonCouncil | undefined {
  if (!value || value === 'london') return undefined;
  if (!Object.hasOwn(authorities,value)) throw new LondonFeedError('Unknown London council',400);
  return value as LondonCouncil;
}

function day(value?: string): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value+'T00:00:00Z');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0,10)!==value) {
    throw new LondonFeedError('Invalid London date',400);
  }
  return value;
}

function bounds(value?: LondonBounds): Record<string,number> {
  if (!value) return {};
  const { west,south,east,north }=value;
  if (![west,south,east,north].every(Number.isFinite) || !(-.7<=west && west<east && east<=.5 && 51.1<=south && south<north && north<=51.9)) {
    throw new LondonFeedError('Viewport is outside the London envelope',400);
  }
  return { west,south,east,north };
}

async function request(kind: string, params: Record<string,unknown>, options: Transport={}) {
  const backend=options.backend || typeof window==='undefined';
  const target=backend ? LONDON_API_ORIGIN+'/'+kind : '/api/planning-feed/london';
  const search=new URLSearchParams(backend ? {} : { kind });
  for (const [key,value] of Object.entries(params)) if (value!==undefined && value!==null && value!=='') search.set(key,String(value));
  const timeout=new AbortController();const timer=setTimeout(()=>timeout.abort(),10000);
  const cancel=()=>timeout.abort();
  if (options.signal?.aborted) cancel();
  else options.signal?.addEventListener('abort',cancel,{once:true});
  try {
    const response=await (options.fetchImpl || fetch)(target+'?'+search,{ signal:timeout.signal,cache:'no-store' });
    if (!response.ok) throw new LondonFeedError('London source feed is unavailable',response.status===400 || response.status===404 ? response.status : 503);
    return await response.json();
  } catch (error) {
    if (error instanceof LondonFeedError) throw error;
    throw new LondonFeedError('London source feed is unavailable');
  } finally { clearTimeout(timer);options.signal?.removeEventListener('abort',cancel); }
}

/** Preserve source dates and identities; never manufacture enrichment fields. */
export function londonPlanningRecord(row: Record<string,any>) {
  const slug=council(row.source_council);
  if (!slug || typeof row.reference_no!=='string' || row.application_uid!==slug+':'+row.reference_no.replace(/\s/g,'').toUpperCase()) {
    throw new LondonFeedError('London source identity could not be verified');
  }
  const location=row.location;
  if (location?.precision==='postcode_centroid') {
    postcodeAttribution(location.provenance?.attribution,1);
    if (location.authority_polygon_verified!==false || location.provenance?.site_boundary_verified!==false ||
        location.provenance?.application_point_precision_not_established!==true ||
        location.provenance?.kind!=='official_source_postcode_centroid') {
      throw new LondonFeedError('London postcode location precision could not be verified');
    }
  }
  const point=location && Number.isFinite(location.latitude) && Number.isFinite(location.longitude) ? location : null;
  return {
    application_uid:row.application_uid,id:row.application_uid,reference_no:row.reference_no,reference:row.reference_no,
    source_council:slug,council_slug:slug,source_system:row.source_system,
    address:row.address ?? null,description:row.description ?? null,status:row.status ?? null,
    received_date:row.received_date ?? null,validated_date:row.validated_date ?? null,
    decision:row.decision ?? null,portal_decision:row.decision ?? null,decision_date:row.decision_date ?? null,
    application_type:row.application_type ?? null,site_postcode:row.site_postcode ?? null,
    link:row.source_url ?? null,source_url:row.source_url ?? null,
    latitude:point?.latitude ?? null,longitude:point?.longitude ?? null,location:point,
    geocode_metadata:point ? { precision:point.precision,authority_polygon_verified:point.authority_polygon_verified===true,
      provider:point.provenance?.source_system ?? row.source_system,location_provenance:point.provenance ?? null,
      coordinate_provenance:point.coordinate_provenance ?? null } : null,
    field_holds:row.field_holds ?? {},source_last_updated:row.source_last_updated ?? null,
    source_date_fields:row.source_date_fields ?? null,description_completeness:row.description_completeness ?? null,
    ...reportedSourceDetail(row),
    ...sourceRefreshDetail(row),
    ...documentDetail(row),
    source:'planning',feed_source:'planning',source_record_only:true,
  };
}

function postcodeAttribution(value: unknown, count: number) {
  if (count>0 && (!Array.isArray(value) || value.length!==LONDON_POSTCODE_ATTRIBUTION.length ||
      value.some((entry,index)=>entry!==LONDON_POSTCODE_ATTRIBUTION[index]))) {
    throw new LondonFeedError('London postcode source attribution could not be verified');
  }
}

export async function readLondonFeed(options: LondonFeedOptions={}) {
  requirePublicLondonScope(options.council);
  if (usesSharedLondonDelivery(options.council || 'london')) {
    return sharedOperation(() => readSharedLondonPayload('applications',sharedLondonFeedParameters(options),options));
  }
  const slug=council(options.council);
  const limit=options.limit ?? 40;
  if (!Number.isInteger(limit) || limit<1 || limit>100) throw new LondonFeedError('London pages require a limit of 1 to 100',400);
  if (options.query && (typeof options.query!=='string' || options.query.length>100)) throw new LondonFeedError('Invalid London query',400);
  if (options.cursor && (typeof options.cursor!=='string' || options.cursor.length>600 || /^\d+$/.test(options.cursor))) throw new LondonFeedError('London requires its own keyset cursor',400);
  const data=await request('applications',{ council:slug,q:options.query,limit,cursor:options.cursor,start:day(options.start),end:day(options.end),...bounds(options.bounds) },options);
  if (!Array.isArray(data.applications) || data.applications.length>limit || !Number.isInteger(data.total) || data.total<data.applications.length || data.date_basis!=='validated' || (data.next_cursor!==null && typeof data.next_cursor!=='string')) {
    throw new LondonFeedError('London source page could not be verified');
  }
  const applications=data.applications.map(londonPlanningRecord);
  if (new Set(applications.map((r:ReturnType<typeof londonPlanningRecord>)=>r.application_uid)).size!==applications.length) throw new LondonFeedError('London page repeats an application');
  if (slug && applications.some((r:ReturnType<typeof londonPlanningRecord>)=>r.source_council!==slug)) throw new LondonFeedError('London council scope changed');
  return { applications,count:applications.length,total:data.total,next_cursor:data.next_cursor,facets:{},partial:false,
    date_basis:'validated' as const,window_start:data.window_start,window_end_exclusive:data.window_end_exclusive };
}

export async function readLondonCoverage(options: Transport={}) {
  requirePublicLondonScope('london');
  if (usesSharedLondonDelivery('london')) return sharedOperation(() => readSharedLondonPayload('coverage',{},options));
  const data=await request('coverage',{},options);
  if (data.publication!=='public' || !Array.isArray(data.councils) || data.councils.length!==33 || new Set(data.councils.map((c:any)=>c.slug)).size!==33 || data.councils.some((c:any)=>!isLondonScope(c.slug) || c.slug==='london' || !Number.isInteger(c.applications) || c.applications<0 || typeof c.daily_verified!=='boolean')) {
    throw new LondonFeedError('London coverage could not be verified');
  }
  if (data.councils.some((c:any)=>(c.mapped_points!==undefined || c.retained_historical_points!==undefined || c.postcode_centroid_points!==undefined) &&
      (![c.source_points,c.mapped_points,c.retained_historical_points,c.postcode_centroid_points ?? 0].every(n=>Number.isInteger(n) && n>=0) ||
       c.mapped_points!==c.source_points+c.retained_historical_points+(c.postcode_centroid_points ?? 0) || c.mapped_points>c.applications))) {
    throw new LondonFeedError('London coverage point counts differ');
  }
  postcodeAttribution(data.location_attributions,data.councils.reduce((n:number,c:any)=>n+(c.postcode_centroid_points ?? 0),0));
  return data;
}

export async function readLondonMap(options: LondonFeedOptions & { bounds:LondonBounds;zoom:number }) {
  requirePublicLondonScope(options.council);
  if (usesSharedLondonDelivery(options.council || 'london')) {
    if (options.limit != null || options.cursor != null) throw new LondonFeedError('Shared maps require complete viewport aggregation',400);
    return sharedOperation(() => readSharedLondonPayload('map',{
      ...sharedLondonFeedParameters(options),map_aggregate_zoom:options.zoom },options));
  }
  if (!options.bounds) throw new LondonFeedError('London map requires a viewport',400);
  const viewport=bounds(options.bounds);
  if (!Number.isFinite(options.zoom) || options.zoom<6 || options.zoom>21) throw new LondonFeedError('Invalid London map zoom',400);
  const data=await request('map',{ ...viewport,zoom:options.zoom,council:council(options.council),q:options.query,start:day(options.start),end:day(options.end) },options);
  if (data.type!=='FeatureCollection' || !Array.isArray(data.features) || data.features.length>12000 || data.complete_for_bounds!==true || !Number.isInteger(data.mapped_applications) || data.mapped_applications<0 || data.features.some((f:any)=>f.type!=='Feature' || f.geometry?.type!=='Point' || !Array.isArray(f.geometry.coordinates) || f.geometry.coordinates.length!==2 || !f.geometry.coordinates.every(Number.isFinite) || !Number.isInteger(f.properties?.count) || f.properties.count<1)) throw new LondonFeedError('London map could not be verified');
  if (data.features.reduce((n:number,f:any)=>n+f.properties.count,0)!==data.mapped_applications) throw new LondonFeedError('London map counts differ');
  if (data.features.some((f:any)=>f.properties.retained_historical_count!==undefined &&
      (!Number.isInteger(f.properties.retained_historical_count) || f.properties.retained_historical_count<0 || f.properties.retained_historical_count>f.properties.count))) {
    throw new LondonFeedError('London historical point counts differ');
  }
  if (data.features.some((f:any)=>f.properties.postcode_centroid_count!==undefined &&
      (!Number.isInteger(f.properties.postcode_centroid_count) || f.properties.postcode_centroid_count<0 ||
       !Number.isInteger(f.properties.approximate_count) || f.properties.approximate_count<f.properties.postcode_centroid_count ||
       f.properties.approximate_count>f.properties.count ||
       f.properties.postcode_centroid_count+(f.properties.retained_historical_count ?? 0)>f.properties.count))) {
    throw new LondonFeedError('London postcode point counts differ');
  }
  const postcodes=data.features.reduce((n:number,f:any)=>n+(f.properties.postcode_centroid_count ?? 0),0);
  if ((postcodes>0 || data.postcode_centroid_applications!==undefined) &&
      (!Number.isInteger(data.postcode_centroid_applications) || data.postcode_centroid_applications!==postcodes)) {
    throw new LondonFeedError('London postcode point counts differ');
  }
  postcodeAttribution(data.location_attributions,postcodes);
  return data;
}

export async function readLondonApplication(uid: string,options:Transport={}) {
  if (typeof uid === 'string') requirePublicLondonScope(uid.split(':')[0]);
  if (usesSharedLondonDelivery(typeof uid === 'string' ? uid.split(':')[0] : '')) {
    return sharedOperation(() => readSharedLondonPayload('detail',{ uid },options));
  }
  if (typeof uid!=='string' || uid.length>160 || !/^[a-z-]+:[^:]{1,140}$/.test(uid) || !council(uid.split(':')[0])) throw new LondonFeedError('Invalid London application identity',400);
  // The proxy keeps the slash-containing source reference in a query value.
  const data=await request(options.backend || typeof window==='undefined' ? 'applications/'+encodeURIComponent(uid) : 'detail',{ uid:options.backend || typeof window==='undefined' ? undefined : uid },options);
  if (data.application_uid!==uid) throw new LondonFeedError('London application identity changed');
  return { ...londonPlanningRecord(data),london_detail_loaded:true };
}
